import { HttpError, useList, useOne } from "@refinedev/core";
import Pagination from "components/pagination";
import Select from "components/select";
import { useEffect, useMemo, useRef, useState } from "react";
import { AddonsCard } from "./card";
import { AddonsTable } from "./table";

const ALL_OPTION = { value: "", label: "All" };

interface AddonsProps {
  currentFilterValues: any;
  currency?: string;
}

interface AddonNameOptionItem {
  addon_name?: string;
  name?: string;
  name_en?: string;
  name_vi?: string;
}

const getAddonName = (item: AddonNameOptionItem | string) => {
  if (typeof item === "string") return item;

  return item.addon_name || item.name_en || item.name || item.name_vi || "";
};

export const Addons = ({ currentFilterValues, currency }: AddonsProps) => {
  const prevFilterKeyRef = useRef<string | null>(null);
  const [addonFilters, setAddonFilters] = useState({
    addon_name: "",
    page: "1",
  });

  const { data, refetch, isLoading } = useList<any>({
    resource: `dashboard/event/${currentFilterValues.event_id}/addons`,
    pagination: {
      mode: "off",
    },
    meta: {
      resource: "data",
      filters: [
        { field: "from_date", operator: "eq", value: currentFilterValues.from_date },
        { field: "to_date", operator: "eq", value: currentFilterValues.to_date },
        { field: "addon_name", operator: "eq", value: addonFilters.addon_name },
        { field: "ticket_platform", operator: "eq", value: currentFilterValues.ticket_platform },
        { field: "currency", operator: "eq", value: localStorage.getItem("currency") ?? "" },
        { field: "page", operator: "eq", value: addonFilters.page },
        { field: "gateway_owner", operator: "eq", value: currentFilterValues.gateway_owner ?? "" },
        { field: "gateway_partner_code", operator: "eq", value: currentFilterValues.gateway_partner_code ?? "" },
        { field: "gateway_name", operator: "eq", value: currentFilterValues.gateway_name ?? "" },
      ],
    },
    queryOptions: {
      enabled: false,
    },
  });

  const { data: addonNamesData, isLoading: isLoadingAddonNames } = useOne<Array<AddonNameOptionItem | string>, HttpError>({
    resource: "dashboard/event",
    id: `${currentFilterValues?.event_id}/addon-names`,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!currentFilterValues.event_id,
    },
  });

  const addonNameOptions = useMemo(() => {
    const names = (addonNamesData?.data ?? [])
      .map(getAddonName)
      .filter((name) => !!name);

    return (names.length > 0 ? [ALL_OPTION] : []).concat(
      names.map((name) => ({ value: name, label: name }))
    );
  }, [addonNamesData?.data]);

  const _handleFilterChange = (key: string, value: any) => {
    const updatedFilter = {
      [key]: value,
      ...(key !== "page" && { page: "1" }),
    };

    setAddonFilters({
      ...addonFilters,
      ...updatedFilter,
    });
  };

  useEffect(() => {
    if (currentFilterValues.event_id) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addonFilters]);

  useEffect(() => {
    const key = JSON.stringify(currentFilterValues);
    if (prevFilterKeyRef.current === null) {
      prevFilterKeyRef.current = key;
      return;
    }
    if (prevFilterKeyRef.current === key) return;
    prevFilterKeyRef.current = key;
    setAddonFilters({
      addon_name: "",
      page: "1",
    });
  }, [currentFilterValues]);

  const tableQueryResult = data;

  return (
    <div className="box-container">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-y-4 lg:flex-row gap-x-2 md:gap-x-3 mb-4">
        <div className="flex-1">
          <label htmlFor="addon-name" className="block text-sm font-medium leading-6">
            Addon Name
          </label>
          <div className="mt-2">
            <Select
              key={`event-addons__${currentFilterValues.event_id}`}
              options={addonNameOptions}
              id="addon-name"
              isLoading={isLoadingAddonNames}
              placeholder="Select Addon"
              className="py-0.5 w-full sm:max-w-full !rounded-md"
              value={addonNameOptions.find((selected) => selected.value === addonFilters.addon_name) || ""}
              onChange={(selected: { label: string; value: string }) => _handleFilterChange("addon_name", selected.value)}
            />
          </div>
        </div>
      </div>
      <div className="sm:hidden">
        <AddonsCard
          data={tableQueryResult?.data}
          pageSize={tableQueryResult?.pageSize}
          currentPage={tableQueryResult?.paging.current_page}
          isLoading={isLoading}
          currency={currency}
        />
      </div>
      <div className="hidden sm:block">
        <AddonsTable
          data={tableQueryResult?.data}
          pageSize={tableQueryResult?.pageSize}
          currentPage={tableQueryResult?.paging.current_page}
          isLoading={isLoading}
          currency={currency}
        />
      </div>
      {tableQueryResult?.paging && tableQueryResult.paging.total_page > 1 ? (
        <div className="mt-6">
          <Pagination
            hideTotal
            currentPage={tableQueryResult?.paging.current_page}
            totalPage={tableQueryResult?.paging.total_page}
            total={{
              totalItems: tableQueryResult?.paging.total_item || 0,
              total: tableQueryResult?.paging.total,
            }}
            onChange={(page: number) => _handleFilterChange("page", page)}
          />
        </div>
      ) : <></>}
    </div>
  );
};
